import React, { useEffect, useState } from 'react';
import { ToastMessage } from '@cokitchen/cokitchen-components';

import { MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import './styles.scss';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { handleApiError } from 'utils/functions';
import { BatchesType } from 'models/batches.model';
import WasteForm, { DefaultBatchType, WastePayloadType } from 'components/base/waste-form';
import { useNavigate } from 'react-router-dom';
import useDashboardContext from 'contexts/dashboard.context';
import { useParams } from 'react-router-dom';
import useRoleContext from 'contexts/role.context';
import { InventoryModule, RolePermission } from 'models/permission.model';

const InventoryStockEdit = () => {
  const { activeStore } = useDashboardContext();
  const { can } = useRoleContext();
  const canWriteInventoryManagement = can(
    InventoryModule.InventoryManagement,
    RolePermission.Write
  );
  const { stock_id } = useParams();
  const [isRecordingWastage, setIsRecordingWastage] = useState(false);
  const [fetchedBatches, setFetchedBatches] = useState<BatchesType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [ingredientDetails, setIngredientDetails] = useState<{
    name: string;
    unitType: string;
  } | null>(null);
  if (!activeStore || !stock_id) {
    return null;
  }
  const [defaultBatch, setDefaultBatch] = useState<DefaultBatchType>({
    batch_no: 0,
    id: ''
  });
  const router = useNavigate();

  const stockId = stock_id.split('_')[0];
  const batchNo = Number(stock_id.split('_')[1]);

  const fetchBatches = async () => {
    if (!stockId || !activeStore.id) return;
    setIsLoading(true);

    try {
      const response = await apiService.get(`ingredients/${stockId}/stores/${activeStore.id}`, {
        version: 'v2'
      });
      if (response.data.data) {
        const batches = response.data.data.main_batches || [];

        const matchedBatch = batches.find((batch: BatchesType) => batch.batch_no === batchNo);

        if (matchedBatch) {
          setDefaultBatch({
            batch_no: matchedBatch.batch_no,
            id: matchedBatch.id
          });
        }
        setIngredientDetails({
          name: response.data.data.name,
          unitType: response.data.data.unit_type
        });
        setFetchedBatches(batches);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBatches();
  }, []);

  const recordWastage = async (data: WastePayloadType) => {
    if (!canWriteInventoryManagement) return;
    if (!stockId || !activeStore.id) return;
    try {
      setIsRecordingWastage(true);

      const wastageData = {
        ingredient_id: stockId,
        store_id: activeStore.id,
        ...data
      };

      const response = await apiService.post('main-store/stores/record-wastage', {
        version: 'v2',
        data: wastageData
      });
      if (response) {
        toast(<ToastMessage message='Wastage recorded successfully' type='success' />);
        router(MAIN_STORE_ROUTE_KEYS.INVENTORY_STOCK);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsRecordingWastage(false);
    }
  };

  return (
    <>
      {isLoading ? (
        <div className='mt-12 flex justify-center items-center'>
          <p>Loading...</p>
        </div>
      ) : fetchedBatches.length === 0 ? (
        <div className='mt-6 flex justify-center items-center'>
          {' '}
          <p>No batches are available </p>
        </div>
      ) : (
        ingredientDetails && (
          <WasteForm
            defaultBatch={defaultBatch}
            ingredientDetails={ingredientDetails}
            submitting={isRecordingWastage}
            onSubmit={recordWastage}
            batches={fetchedBatches}
            onCancel={() => router(-1)}
            canWrite={canWriteInventoryManagement}
          />
        )
      )}
    </>
  );
};

export default InventoryStockEdit;
